// cleanup_convert_to_typescript_file_GENERICS_TRANSFERS

import React, { useContext, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';

import { FormContext } from '@glu/validation-react';
import gluLocale from '@glu/locale';
import { createUseStyles } from '@glu/theming';
import {
  END_DATE,
  FURTHER_NOTICE,
  ValidatedScheduler,
  defaultStrings_en_US as defaultSchedulerStrings,
  COMPLEX
} from '@glu/scheduler-react';
import { defaultStrings_en_US as defaultDateStrings } from '@glu/datepicker-react';

import { getAllowedTransferDates } from 'api/transfers/service';
import { DATE_FORMAT } from 'constants.js';
import BeforeAfterDayRadio from 'components/beforeAfterDayRadio/BeforeAfterDayRadio';
import usePlaceResolver, { variants } from 'components/usePlaceResolver/usePlaceResolver';
import { calendarMonths } from 'components/locale/dateTime.locale';
import LocaleContext from 'components/locale/LocaleContext';
import { FrequencyType } from 'components/loanPaymentTemplate/types/form/scheduler';

import styles from './recurringTransfer.styles';

const useStyles = createUseStyles(styles);

export const RecurringTransferScheduler = ({ disabled = false, readOnly }) => {
  const classes = useStyles(styles);
  const locale = useContext(LocaleContext);

  gluLocale.set({
    ...defaultSchedulerStrings,
    ...defaultDateStrings,
    ...calendarMonths,
    'validator.required': locale['validation.required']
  });
  const {
    data,
    data: { recurring },
    errors: { recurring: recurringFormErrors },
    onChange: setValue,
    validate
  } = useContext(FormContext);

  useEffect(() => {
    const endDateError = (recurringFormErrors || []).filter((error) => error.match(/End date/));
    const hasEndDateError = endDateError.length > 0;
    if (recurring?.complexType === FURTHER_NOTICE && hasEndDateError) {
      validate('recurring');
    }
  }, [recurring?.complexType]);

  const services = useMemo(() => [getAllowedTransferDates()], []);
  const { Placeholder, results } = usePlaceResolver({
    services,
    variant: variants.input
  });

  const [calendar = {}] = results;

  if (Placeholder) {
    return <Placeholder />;
  }

  const isSelectableDate = (day) => {
    const holidays = calendar?.holidays || [];
    const businessDaysOfWeek = calendar?.businessDaysOfWeek || [];
    const isHoliday = holidays.some((d) => day.isSame(moment(d), 'day'));

    if (isHoliday) {
      return true;
    }
    if (!businessDaysOfWeek[day.day()]) {
      return true;
    }
    return false;
  };

  // We don't (currently) support 'Day of Week' dropdowns for semi monthly
  const semiMonthlyDowHack = (classes) => (data) =>
    [data?.recurring?.type, data?.recurring?.value?.type].includes(FrequencyType.SEMIMONTHLY)
      ? classes.semiMonthly
      : '';

  const getEndsComplexOptions = () => {
    if (readOnly) {
      const { endValue } = data?.scheduling || {};
      return [endValue ? END_DATE : FURTHER_NOTICE];
    }
    return [END_DATE, FURTHER_NOTICE];
  };

  const endDate = recurring?.value ? recurring.value?.endValue : recurring?.endValue;

  return (
    <>
      <ValidatedScheduler
        {...data?.scheduling}
        className={classNames(classes.schedulerWeeklyFix, semiMonthlyDowHack(classes)(data))}
        daily={false}
        dataQa="recurring-transfer"
        dateFormat={DATE_FORMAT}
        DatePickerEndProps={{
          // This is to set the date field show blank when endsComplexOptions radio is set to value other than "End by selected date"
          value: endDate ? [moment(endDate), moment(endDate)] : []
        }}
        DatePickerProps={{
          dateMin: moment.max(moment().add(1, 'day'), moment(calendar.earliestDayForTransfer)),
          isSelectableDate
        }}
        disabled={disabled}
        ends={COMPLEX}
        endsComplexOptions={getEndsComplexOptions()}
        htmlId="recurring-transfer"
        monthly={{
          canSetTime: false,
          enabled: true,
          multipleDays: false
        }}
        name="recurring"
        semiMonthly={{
          canSetTime: false,
          enabled: true,
          multipleDays: true
        }}
        starts
        validators={[
          {
            msg: locale['validation.required'],
            required: true
          }
        ]}
        weekly={{
          allowWeekend: true,
          canSetTime: false,
          enabled: true,
          minDays: 1
        }}
      />
      <BeforeAfterDayRadio futureDated={data?.futureDated} onChange={setValue} setInitial />
    </>
  );
};

RecurringTransferScheduler.propTypes = {
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool.isRequired
};

export default RecurringTransferScheduler;
